import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';
import translations from '../i18n/translations';

const AppContext = createContext({});

export const useApp = () => useContext(AppContext);

export const useT = () => {
  const { lang } = useContext(AppContext);
  return (section, key) => translations[lang]?.[section]?.[key] ?? translations['fr'][section]?.[key] ?? key;
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.removeAttribute('data-theme');
  } else if (theme === 'light') {
    root.setAttribute('data-theme', 'light');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', 'light');
  }
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('fr');
  const [theme, setTheme] = useState('dark');
  const [density, setDensity] = useState('Normale');

  useEffect(() => {
    // 1. Charger les préférences
    const cachedPrefs = localStorage.getItem('ako_prefs');
    if (cachedPrefs) {
      try {
        const p = JSON.parse(cachedPrefs);
        if (p.langue) setLang(p.langue);
        if (p.theme) { setTheme(p.theme); applyTheme(p.theme); }
        if (p.density) setDensity(p.density);
      } catch {}
    }

    // 2. Charger l'utilisateur via le token
    const token = localStorage.getItem('ako_token');
    if (token) {
      api.getMe()
        .then(res => {
          if (res.user) {
            setUser(res.user);
          } else {
            localStorage.removeItem('ako_token');
          }
        })
        .catch(() => {
          localStorage.removeItem('ako_token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('ako_token', token);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('ako_token');
    window.location.href = '/login';
  };

  const updateTheme = (t) => {
    setTheme(t);
    applyTheme(t);
  };

  return (
    <AppContext.Provider value={{ 
      user, setUser, loading, login, logout,
      lang, setLang, theme, setTheme: updateTheme, density, setDensity 
    }}>
      {children}
    </AppContext.Provider>
  );
};
